import axios from "axios";
import { useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { loginAuthAPI } from "@/auth/api/loginAuthAPI";
import { AuthContext } from "@/auth/context";
import { Types } from "@/auth/types";
import {
  LoginErrorResponse,
  LoginFailure,
  LoginFailureError,
  LoginAttemptStatus,
} from "@/auth/types/LoginAuth";
import { getDeviceId } from "@/lib/fingerprint";
import { paths } from "@/routes/paths";

const DEFAULT_LOGIN_ERROR_MESSAGE =
  "Error signing in. Please check your credentials and try again.";

const parseAttemptsLeft = (
  attemptsLeft: LoginErrorResponse["attempts_left"],
): number | undefined => {
  if (typeof attemptsLeft === "number" && Number.isFinite(attemptsLeft)) {
    return attemptsLeft;
  }

  if (typeof attemptsLeft === "string") {
    const parsed = Number.parseInt(attemptsLeft, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
  }

  return undefined;
};

const normalizeLoginFailure = (error: unknown): LoginFailure => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as LoginErrorResponse | undefined;
    const status =
      data?.status === "warning" || data?.status === "blocked"
        ? data.status
        : "error";

    return {
      status: status as LoginAttemptStatus,
      detail: data?.detail || DEFAULT_LOGIN_ERROR_MESSAGE,
      attemptsLeft: parseAttemptsLeft(data?.attempts_left),
    };
  }

  return {
    status: "error",
    detail: DEFAULT_LOGIN_ERROR_MESSAGE,
  };
};

export const useAuthLogin = () => {
  const { dispatch } = useContext(AuthContext);
  const navigate = useNavigate();

  return useCallback(
    async (username: string, password: string) => {
      try {
        const deviceId = await getDeviceId();

        // Step 1: Get access and refresh tokens
        const tokenResponse = await loginAuthAPI.login({
          username,
          password,
          device_id: deviceId,
          // hcaptcha_token is temporarily disabled.
        });

        // Store tokens in localStorage
        localStorage.setItem("access_token", tokenResponse.access);
        localStorage.setItem("refresh_token", tokenResponse.refresh);

        // Step 2: Get user profile
        const profile = await loginAuthAPI.getProfile();

        // Store user data in localStorage
        localStorage.setItem("user", JSON.stringify(profile));

        // Update auth context with user data
        dispatch({
          type: Types.AUTH_WITH_ONEID, // Reusing existing type
          payload: {
            user: {
              id: profile.id,
              full_name: profile.full_name || "",
              access_token: tokenResponse.access,
              role: profile.role,
              region: profile.region,
              city: profile.city,
              makhalla: profile.makhalla,
            },
          },
        });

        toast.success("You have successfully signed in.");
        navigate(paths.dashboard.root);
      } catch (error: unknown) {
        throw new LoginFailureError(normalizeLoginFailure(error));
      }
    },
    [dispatch, navigate],
  );
};
